import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import type {SxProps} from '@mui/system';
import type {FC} from 'react';
import {useTranslation} from 'react-i18next';
import Title from '@/components/common/Title';
import ForestMassStatuses from '@/components/ForestMassDetail/ForestMassStatuses';
import GeneralInfo from '@/components/ForestMassDetail/GeneralInfo';
import MapDetail from '@/components/ForestMassDetail/MapDetail';
import SpeciesLegend from '@/components/ForestMassDetail/SpeciesLegend';
import type {Appraiser} from '@/domain/entities/Appraiser';
import type {ForestMass} from '@/domain/entities/ForestMass';
import type {ForestMassSpecies} from '@/domain/entities/ForestMassSpecies';
import type {ForestMassStatus} from '@/domain/entities/ForestMassStatus';
import type {InventoryPlot} from '@/domain/entities/InventoryPlot';
import type {LidarTechnology} from '@/domain/entities/LidarTechnology';
import type {CreateInventoryPlot, CutForestMass} from '@/domain/entities/Op';
import type {Species} from '@/domain/entities/Species';
import type {UavPointCloud, UavPointCloudCreate} from '@/domain/entities/UavPointCloud';
import type BusinessLogicBasedOnStatuses from '@/domain/useCases/businessLogicBasedOnStatuses';
import type UserPermissions from '@/domain/useCases/userPermissions';

//TYPES
export type Props = {
  forestMass: ForestMass;
  forestMassStatus: ForestMassStatus;
  forestMassSpecies?: Array<ForestMassSpecies>;
  appraisers: Array<Appraiser>;
  species: Array<Species>;
  selectedSpecies: Array<Species>;
  lidarTechnologies?: Array<LidarTechnology>;
  uavPointCloud?: UavPointCloud;
  inventoryPlots?: Array<InventoryPlot>;
  permissions: UserPermissions;
  businessLogic: BusinessLogicBasedOnStatuses;
  onBack: () => void;
  onInventoryPlotSelect: (uuid: string) => void;
  onForestMassChange: (forestMass: ForestMass) => void;
  onSelectedSpeciesChange: (uuids: Array<string>) => void;
  onCutForestMass: (domain: CutForestMass) => void;
  onDownloadForestMass?: (forestMass: ForestMass) => void;
  onUploadInventoryPlot: (domain: CreateInventoryPlot, file: File) => void;
  onUploadUavPointCloud: (domain: Partial<UavPointCloudCreate>, file: File) => void;
  onDeleteUavPointCloud: (pointCloudUuid: string) => void;
  onDownloadUavPointCloud: (pointCloudUuid: string) => void;
  onReuploadUavPointCloud: (pointCloudUuid: string, file: File) => void;
};

const ForestMassDetail: FC<Props> = ({
  forestMass,
  forestMassStatus,
  forestMassSpecies,
  appraisers,
  species,
  selectedSpecies,
  lidarTechnologies,
  uavPointCloud,
  inventoryPlots,
  permissions,
  businessLogic,
  onForestMassChange,
  onSelectedSpeciesChange,
  onCutForestMass,
  onBack,
  onInventoryPlotSelect,
  onDownloadForestMass,
  onUploadInventoryPlot,
  onUploadUavPointCloud,
  onDownloadUavPointCloud,
  onDeleteUavPointCloud,
  onReuploadUavPointCloud,
}) => {
  const {t} = useTranslation();

  const handleDownload = () => {
    onDownloadForestMass?.(forestMass);
  };
  const {boundary} = forestMass;
  return (
    <Stack direction="column" sx={styles.container}>
      <Stack direction="row" sx={styles.titleContainer}>
        <Title onBack={onBack} label={t('forestMass.detailTitle')} />
        <Button variant="contained" onClick={handleDownload}>
          {t('download')}
        </Button>
      </Stack>
      <Stack direction="row" sx={styles.content}>
        <Paper sx={styles.info} elevation={0}>
          <GeneralInfo
            appraisers={appraisers}
            forestMass={forestMass}
            species={species}
            selectedSpecies={selectedSpecies}
            permissions={permissions}
            onForestMassChange={onForestMassChange}
            onSelectedSpeciesChange={onSelectedSpeciesChange}
          />
        </Paper>
        <Paper sx={styles.status} elevation={0}>
          <ForestMassStatuses
            forestMass={forestMass}
            forestMassStatus={forestMassStatus}
            lidarTechnologies={lidarTechnologies}
            uavPointCloud={uavPointCloud}
            onCutForestMass={onCutForestMass}
            onUploadUavPointCloud={onUploadUavPointCloud}
            onUploadInventoryPlot={onUploadInventoryPlot}
            onDownloadUavPointCloud={onDownloadUavPointCloud}
            onDeleteUavPointCloud={onDeleteUavPointCloud}
            onReuploadUavPointCloud={onReuploadUavPointCloud}
            permissions={permissions}
            businessLogic={businessLogic}
          />
        </Paper>
        <Paper sx={styles.map}>
          <MapDetail
            species={species}
            forestMassSpecies={forestMassSpecies}
            boundary={boundary}
            inventoryPlots={inventoryPlots}
            onInventoryPlotClick={onInventoryPlotSelect}
          />
          <Stack>
            <SpeciesLegend species={species} />
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
};
export default ForestMassDetail;

const styles: Record<string, SxProps> = {
  container: {
    alignItems: 'flex-start',
    gap: 1,
    height: '100%',
    minWidth: 0,
  },
  titleContainer: {
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 1,
    width: '100%',
  },
  content: {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      sm: '1fr',
      md: 'minmax(390px, 1fr) minmax(390px, 1fr)',
      lg: '1fr 1fr 1fr',
    },
    gap: 2,
    minWidth: 0,
    maxWidth: '100%',
    minHeight: 0,
    alignItems: 'stretch',
    height: '100%',
    width: '100%',
    '& > :nth-child(3)': {
      gridColumn: {
        xs: 'auto',
        sm: '1 / -1',
        lg: 'auto',
      },
    },
  },
  info: {
    p: 2,
    pl: 3,
    height: '100%',
  },
  status: {
    p: 2,
    pl: 4,
  },
  map: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 300,
    aspectRatio: '4 / 2',
    p: 2,
  },
};
